/**
 * Wizard state shape for the Monitor setup flow.
 *
 * Five steps mirror the root skill's ui_flow.steps declaration:
 *   source / source_config / destination / schedule / review
 *
 * Lives entirely in component state for v1 — saved server-side only on
 * the final Step 5 submit (POST /v1/admin/import-jobs).
 */
import type { ImportCapability, ImportCapabilitySkill } from '../../../../types/importCapabilities';

export type WizardStep =
  | 'source'
  | 'source_config'
  | 'destination'
  | 'schedule'
  | 'review';

export const WIZARD_STEPS: WizardStep[] = [
  'source',
  'source_config',
  'destination',
  'schedule',
  'review',
];

export const WIZARD_STEP_LABELS: Record<WizardStep, string> = {
  source: 'Source',
  source_config: 'Configure',
  destination: 'Destination',
  schedule: 'Schedule',
  review: 'Review',
};

export interface WizardState {
  step: WizardStep;
  /** Skill chosen in Step 1 (Sources). */
  selectedSkill: ImportCapabilitySkill | null;
  /** Capability chosen on the selected skill (always import_* for v1). */
  selectedCapability: ImportCapability | null;
  /** Step 2 — user-entered values keyed by field_id. */
  sourceConfig: Record<string, unknown>;
  /** Step 3 — destination side, opaque dict for now. */
  destinationConfig: Record<string, unknown>;
  /** Step 4 — { cadence, timezone }. */
  scheduleConfig: Record<string, unknown>;
  /** Step 5 — user-given monitor name (the human label shown in lists +
   *  admin tooling, distinct from the destination collection's name). */
  monitorName: string;
  /** True once the user has typed into the Step 5 monitor-name field. While
   *  false, the monitor name mirrors the new-collection name so the user is
   *  not asked for essentially the same label twice; once true, an explicit
   *  Step 5 rename wins and the mirror stops. */
  monitorNameTouched: boolean;
}

export type WizardAction =
  | { type: 'go_to'; step: WizardStep }
  | {
      type: 'select_source';
      skill: ImportCapabilitySkill;
      capability: ImportCapability;
    }
  | { type: 'update_source_config'; values: Record<string, unknown> }
  | { type: 'update_destination_config'; values: Record<string, unknown> }
  | { type: 'update_schedule_config'; values: Record<string, unknown> }
  | { type: 'set_monitor_name'; name: string }
  | { type: 'reset' };

export const initialWizardState: WizardState = {
  step: 'source',
  selectedSkill: null,
  selectedCapability: null,
  sourceConfig: {},
  destinationConfig: {},
  scheduleConfig: {},
  monitorName: '',
  monitorNameTouched: false,
};

export const wizardReducer = (
  state: WizardState,
  action: WizardAction,
): WizardState => {
  switch (action.type) {
    case 'go_to':
      return { ...state, step: action.step };
    case 'select_source': {
      // Reset source_config only when the capability actually changes —
      // preserves typed values when the user goes Back → re-selects the
      // same source. The new capability's schema may share no field ids
      // with the previous one, so a real change must clear.
      const sameCapability =
        state.selectedSkill?.skill_name === action.skill.skill_name &&
        state.selectedCapability?.capability_id ===
          action.capability.capability_id;
      let sourceConfig = sameCapability ? state.sourceConfig : {};
      if (!sameCapability) {
        // Seed declared defaults so required-validation and the rendered
        // primitive (switch off, date_window preset selected) agree on
        // first render. Without this, a required field with a default
        // would visually look filled but `findMissingRequired` would
        // block Continue until the user re-touched it.
        const seeded: Record<string, unknown> = {};
        for (const f of action.capability.config_schema?.fields ?? []) {
          if (f.default !== undefined) {
            seeded[f.id] = f.default;
            continue;
          }
          // Switches have no "unset" rendered state — the primitive falls
          // back to false when value is undefined. Mirror that in wizard
          // state so required-validation agrees with the UI and the field
          // persists with the value the user actually sees.
          if (f.type === 'switch') {
            seeded[f.id] = false;
          }
        }
        sourceConfig = seeded;
      }
      return {
        ...state,
        selectedSkill: action.skill,
        selectedCapability: action.capability,
        step: 'source_config',
        sourceConfig,
      };
    }
    case 'update_source_config':
      return { ...state, sourceConfig: { ...state.sourceConfig, ...action.values } };
    case 'update_destination_config': {
      const destinationConfig = {
        ...state.destinationConfig,
        ...action.values,
      };
      // "Monitor name asked twice": in new-collection mode the user names the
      // collection here, then was asked again for a monitor name in Step 5.
      // Mirror the collection name into the monitor name until the user
      // explicitly edits the Step 5 field (monitorNameTouched), so the same
      // label is only typed once but a deliberate rename still wins.
      const monitorName =
        !state.monitorNameTouched &&
        typeof action.values.collection_name === 'string'
          ? action.values.collection_name
          : state.monitorName;
      return { ...state, destinationConfig, monitorName };
    }
    case 'update_schedule_config':
      return {
        ...state,
        scheduleConfig: { ...state.scheduleConfig, ...action.values },
      };
    case 'set_monitor_name':
      return { ...state, monitorName: action.name, monitorNameTouched: true };
    case 'reset':
      return initialWizardState;
    default:
      return state;
  }
};
